// src/utils/api.js
import axios from "axios";

/**
 * Ensure base URL always ends with /api
 * Accepts:
 *  - REACT_APP_API_URL=http://localhost:5003
 *  - REACT_APP_API_URL=http://localhost:5003/api
 */
function buildBaseUrl() {
  const raw = (process.env.REACT_APP_API_URL || "http://localhost:5003").trim();

  // remove trailing slash
  const cleaned = raw.replace(/\/+$/, "");

  // if already ends with /api, keep it
  if (cleaned.endsWith("/api")) return cleaned;

  // otherwise append /api
  return `${cleaned}/api`;
}

const API_URL = buildBaseUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    config.headers = config.headers || {};
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle token expiration + normalize errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 401: Token expired / invalid
    if (error?.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
      return Promise.reject(new Error("Session expired. Please log in again."));
    }

    // Normalize backend errors
    const apiMsg =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      "Request failed";

    return Promise.reject(new Error(apiMsg));
  }
);

/* ======================================================
   Convenience wrappers (services stay clean)
   - Returns response.data directly
====================================================== */
export const apiGet = async (url, config) => {
  const res = await api.get(url, config);
  return res.data;
};

export const apiPost = async (url, data, config) => {
  const res = await api.post(url, data, config);
  return res.data;
};

export const apiPut = async (url, data, config) => {
  const res = await api.put(url, data, config);
  return res.data;
};

export const apiPatch = async (url, data, config) => {
  const res = await api.patch(url, data, config);
  return res.data;
};

export const apiDelete = async (url, config) => {
  const res = await api.delete(url, config);
  return res.data;
};

export default api;